export const M4_UNIT_7 = {
  id: "m4-u7-advanced-feelings",
  title: "Unidad 7: Feeling This & That",
  topic: "Level up your emotional expression! Learn intensifiers (very, really, so, quite) and native expressions to sound more natural. Master slang, idioms, and phrasal verbs for real conversations!",
  iconType: "star",
  grammarImage: "/grammar/grammar-m4-u7.jpg",
  grammarText: "Intensifiers modify adjectives: VERY tired (muy), REALLY happy (realmente), SO excited (tan), QUITE good (bastante), PRETTY nice (bastante), A BIT sad (un poco). Order: intensifier + adjective. 'I'm SO tired' ✓, 'I'm tired SO' ❌",
  
  sets: [
    // SET 1: Basic Intensifiers Introduction
    {
      id: "m4-u7-set1",
      title: "Set 1: Making Feelings Stronger",
      questions: [
        {
          type: "match",
          q: "Match intensifier to meaning:",
          pairs: [
            { left: "very", right: "muy" },
            { left: "really", right: "realmente/muy" },
            { left: "so", right: "tan/muy (emphasis)" }
          ],
          a: "completed",
          tip: "Intensifiers = palabras que hacen el adjetivo más fuerte! Very/really/so = todos significan 'muy' pero con diferente énfasis."
        },
        {
          type: "mc",
          q: "'I'm ___ tired today!'",
          options: ["very", "tired very", "very much"],
          a: "very",
          tip: "Order: intensifier + adjective. 'Very tired' ✓, 'tired very' ❌"
        },
        {
          type: "input",
          q: "Complete: 'She's ___ happy about the news!' (really)",
          a: "really"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy tan emocionado'",
          words: ["I'm", "so", "excited!"],
          a: "I'm so excited!"
        },
        {
          type: "mc",
          q: "'I feel ___ stressed right now.'",
          options: ["really", "stressed really", "much"],
          a: "really"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ angry about what happened.' (very)",
          a: "very"
        },
        {
          type: "mc",
          q: "'That test was ___ difficult!'",
          options: ["so", "difficult so", "so much"],
          a: "so",
          successtip: "✓ SO! Emphasis! 💥 'So' = tan/muy with emotion/surprise. More dramatic than 'very'! Native speakers love 'so'!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy muy cansado hoy'",
          words: ["I'm", "really", "tired", "today."],
          a: "I'm really tired today."
        },
        {
          type: "input",
          q: "Complete: 'We're ___ excited for the weekend!' (so)",
          a: "so"
        },
        {
          type: "mc",
          q: "Which sounds MORE emotional?",
          options: ["I'm SO happy!", "I'm very happy.", "I'm happy."],
          a: "I'm SO happy!",
          tip: "Emotion level: happy < very happy < SO happy! 'So' = most enthusiastic!"
        }
      ]
    },

    // SET 2: Quite, Pretty, Kind of (Medium Intensity)
    {
      id: "m4-u7-set2",
      title: "Set 2: Medium Intensity",
      questions: [
        {
          type: "match",
          q: "Match medium intensifier:",
          pairs: [
            { left: "quite", right: "bastante (formal)" },
            { left: "pretty", right: "bastante (casual)" },
            { left: "kind of / kinda", right: "como que/algo (casual)" }
          ],
          a: "completed",
          successtip: "✓ LEVELS! Intensity scale! 📊 a bit < kind of < pretty/quite < very < really < SO! Know your levels!"
        },
        {
          type: "mc",
          q: "'I'm ___ tired, but I can still work.' (medium - not very)",
          options: ["pretty", "so", "extremely"],
          a: "pretty",
          tip: "'Pretty tired' = bastante cansado (not extreme). Casual and common!"
        },
        {
          type: "input",
          q: "Complete: 'The movie was ___ good.' (quite - formal)",
          a: "quite"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy como que nervioso'",
          words: ["I'm", "kind", "of", "nervous."],
          a: "I'm kind of nervous.",
          successtip: "✓ KIND OF! Super casual! 🗣️ Native speakers say 'kinda' (contraction). 'I'm kinda tired' = very common slang!"
        },
        {
          type: "mc",
          q: "'This job is ___ stressful, but manageable.'",
          options: ["pretty", "so", "extremely"],
          a: "pretty"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ good at English.' (quite)",
          a: "quite"
        },
        {
          type: "mc",
          q: "Casual conversation: 'How are you?' - 'I'm ___ okay, I guess.'",
          options: ["kinda", "so", "extremely"],
          a: "kinda",
          tip: "'Kinda okay' = not great, not terrible. 'I guess' adds uncertainty. Very native!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy bastante ocupado esta semana'",
          words: ["I'm", "pretty", "busy", "this", "week."],
          a: "I'm pretty busy this week."
        },
        {
          type: "input",
          q: "Complete: 'She's ___ happy with her new apartment.' (quite)",
          a: "quite"
        },
        {
          type: "mc",
          q: "Which is LEAST intense?",
          options: ["I'm kind of tired", "I'm very tired", "I'm SO tired"],
          a: "I'm kind of tired",
          tip: "Kind of = lowest intensity = un poquito. Very = medium. SO = highest!"
        }
      ]
    },

    // SET 3: A Bit, A Little, Not Very (Low Intensity)
    {
      id: "m4-u7-set3",
      title: "Set 3: Softening Feelings",
      questions: [
        {
          type: "match",
          q: "Match low intensity expression:",
          pairs: [
            { left: "a bit", right: "un poco" },
            { left: "a little", right: "un poco" },
            { left: "not very", right: "no muy" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I'm ___ tired, but I'm okay.' (just a little)",
          options: ["a bit", "so", "extremely"],
          a: "a bit",
          tip: "'A bit' = un poquito. Softens/downplays the feeling. Very British/Canadian!"
        },
        {
          type: "input",
          q: "Complete: 'She's ___ nervous about the interview.' (a little)",
          a: "a little"
        },
        {
          type: "order",
          q: "Ordena: 'No estoy muy feliz con la decisión'",
          words: ["I'm", "not", "very", "happy", "with", "the", "decision."],
          a: "I'm not very happy with the decision."
        },
        {
          type: "mc",
          q: "Polite complaint: 'I'm ___ upset about the delay.'",
          options: ["a bit", "extremely", "so"],
          a: "a bit",
          successtip: "✓ POLITE! Canadian style! 🇨🇦 'A bit upset' = downplaying anger. Canadians soften complaints! Very cultural!"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ worried, but not too much.' (a little)",
          a: "a little"
        },
        {
          type: "mc",
          q: "'The food was okay, but ___ cold.'",
          options: ["a bit", "so", "really"],
          a: "a bit"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy un poco cansado de esperar'",
          words: ["I'm", "a", "bit", "tired", "of", "waiting."],
          a: "I'm a bit tired of waiting.",
          tip: "'Tired OF' = phrasal expression! Harto de algo. 'I'm tired of my job' = common!"
        },
        {
          type: "input",
          q: "Complete: 'We're ___ disappointed.' (not very - showing it's minor)",
          a: "not very"
        },
        {
          type: "mc",
          q: "Understatement: 'I'm ___ concerned about my visa status.' (downplaying worry)",
          options: ["a little", "SO", "extremely"],
          a: "a little"
        },
        {
          type: "mc",
          q: "British/Canadian politeness: Translate 'Estoy MUY enojado' politely:",
          options: [
            "I'm a bit upset (downplaying)",
            "I'm SO angry!",
            "I'm extremely mad!"
          ],
          a: "I'm a bit upset (downplaying)",
          successtip: "✓ UNDERSTATEMENT! Cultural key! 😅 Canadians/Brits downplay negative emotions to be polite. 'A bit upset' might mean VERY upset!"
        }
      ]
    },

    // SET 4: Extreme Intensifiers (Slang)
    {
      id: "m4-u7-set4",
      title: "Set 4: Extreme Feelings (Slang)",
      questions: [
        {
          type: "match",
          q: "Match extreme slang intensifier:",
          pairs: [
            { left: "super", right: "súper (casual)" },
            { left: "totally", right: "totalmente/súper" },
            { left: "absolutely", right: "absolutamente (formal)" }
          ],
          a: "completed",
          successtip: "✓ SLANG POWER! Native expressions! 🔥 Super/totally = muy casual. Absolutely = formal. Know your context!"
        },
        {
          type: "mc",
          q: "Casual slang: 'I'm ___ tired, I can't move!'",
          options: ["super", "a bit", "quite"],
          a: "super",
          tip: "'Super tired' = muy muy cansado. Very casual, very common!"
        },
        {
          type: "input",
          q: "Complete: 'She's ___ exhausted after that shift.' (totally)",
          a: "totally"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy absolutamente agotado'",
          words: ["I'm", "absolutely", "exhausted."],
          a: "I'm absolutely exhausted."
        },
        {
          type: "mc",
          q: "Gen Z slang: 'That movie was ___ boring!' (extremely)",
          options: ["totally", "a little", "not very"],
          a: "totally"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ stressed about money.' (super)",
          a: "super"
        },
        {
          type: "mc",
          q: "Formal context: 'I'm ___ certain about my decision.'",
          options: ["absolutely", "super", "kinda"],
          a: "absolutely",
          tip: "'Absolutely' = formal intensifier. Use in professional contexts! 'Absolutely sure' ✓"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy súper emocionado por el concierto'",
          words: ["I'm", "super", "excited", "for", "the", "concert!"],
          a: "I'm super excited for the concert!"
        },
        {
          type: "input",
          q: "Complete: 'We're ___ thrilled about the news!' (absolutely)",
          a: "absolutely"
        },
        {
          type: "mc",
          q: "Canadian youth slang: 'I'm ___ dead tired!' (completely exhausted)",
          options: ["literally", "a bit", "quite"],
          a: "literally",
          successtip: "✓ LITERALLY! Slang overuse! 😂 'I'm literally dying' (not really dying). Gen Z/Millennial exaggeration! Super common!"
        }
      ]
    },

    // SET 5: Phrasal Verbs with Feelings
    {
      id: "m4-u7-set5",
      title: "Set 5: Phrasal Verbs for Emotions",
      questions: [
        {
          type: "match",
          q: "Match phrasal verb to meaning:",
          pairs: [
            { left: "fed up with", right: "harto de" },
            { left: "stressed out", right: "muy estresado" },
            { left: "burned out", right: "agotado (trabajo)" }
          ],
          a: "completed",
          successtip: "✓ PHRASAL POWER! Native expressions! 💪 These are HOW natives actually talk! Memorize them!"
        },
        {
          type: "mc",
          q: "'I'm so ___ with my job, I want to quit!'",
          options: ["fed up", "happy", "excited"],
          a: "fed up",
          tip: "'Fed up WITH' = harto de. Very common complaint expression! 'Fed up with traffic' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She's totally ___ from working overtime.' (stressed out)",
          a: "stressed out"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy agotado del trabajo'",
          words: ["I'm", "burned", "out", "from", "work."],
          a: "I'm burned out from work.",
          successtip: "✓ BURNOUT! Modern epidemic! 🔥 'Burned out' = agotamiento total por trabajo. Very relevant for immigrants working multiple jobs!"
        },
        {
          type: "mc",
          q: "'He's ___ about his English test tomorrow.' (nervous/anxious)",
          options: ["freaking out", "fed up", "burned out"],
          a: "freaking out",
          tip: "'Freaking out' = muy nervioso/asustado. Slang but very common! 'I'm freaking out!' = panic!"
        },
        {
          type: "input",
          q: "Complete: 'I'm ___ with this situation!' (fed up)",
          a: "fed up"
        },
        {
          type: "mc",
          q: "'She totally ___ when she heard the news.' (reacted emotionally)",
          options: ["broke down", "fed up", "stressed out"],
          a: "broke down",
          successtip: "✓ BROKE DOWN! Emotional collapse! 😢 'Broke down crying' = lloró sin control. 'My car broke down' = different meaning!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy harto de esperar por mi permiso de trabajo'",
          words: ["I'm", "fed", "up", "with", "waiting", "for", "my", "work", "permit."],
          a: "I'm fed up with waiting for my work permit."
        },
        {
          type: "input",
          q: "Complete: 'He's ___ over the deadline.' (stressed out)",
          a: "stressed out"
        },
        {
          type: "mc",
          q: "'I'm completely ___ after this week!' (exhausted - phrasal)",
          options: ["burned out", "fed up", "freaking out"],
          a: "burned out"
        }
      ]
    },

    // SET 6: Idioms & Native Expressions
    {
      id: "m4-u7-set6",
      title: "Set 6: Feeling Idioms",
      questions: [
        {
          type: "match",
          q: "Match idiom to meaning:",
          pairs: [
            { left: "over the moon", right: "súper feliz" },
            { left: "down in the dumps", right: "muy triste/deprimido" },
            { left: "on edge", right: "nervioso/tenso" }
          ],
          a: "completed",
          successtip: "✓ IDIOMS! Sound native! 🎯 These are HOW English speakers express extreme emotions naturally!"
        },
        {
          type: "mc",
          q: "'I'm ___ about getting my PR!' (extremely happy - idiom)",
          options: ["over the moon", "down in the dumps", "on edge"],
          a: "over the moon",
          tip: "'Over the moon' = súper feliz (idiom). British origin, used in Canada! Very expressive!"
        },
        {
          type: "input",
          q: "Complete: 'She's feeling ___ lately.' (down in the dumps - sad)",
          a: "down in the dumps"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy súper feliz por las noticias'",
          words: ["I'm", "over", "the", "moon", "about", "the", "news!"],
          a: "I'm over the moon about the news!"
        },
        {
          type: "mc",
          q: "'He's been ___ all week waiting for the interview call.' (nervous - idiom)",
          options: ["on edge", "over the moon", "down in the dumps"],
          a: "on edge"
        },
        {
          type: "input",
          q: "Complete: 'I'm feeling a bit ___ today.' (down in the dumps)",
          a: "down in the dumps"
        },
        {
          type: "mc",
          q: "Idiom: 'I'm on ___ seven!' (extremely happy)",
          options: ["cloud", "top", "sky"],
          a: "cloud",
          successtip: "✓ CLOUD NINE! Classic idiom! ☁️ 'On cloud nine' = felicísimo. From 1950s American slang, now universal!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy muy nervioso esperando los resultados'",
          words: ["I'm", "on", "edge", "waiting", "for", "the", "results."],
          a: "I'm on edge waiting for the results."
        },
        {
          type: "input",
          q: "Complete: 'She was ___ when she got accepted!' (on cloud nine)",
          a: "on cloud nine"
        },
        {
          type: "mc",
          q: "Idiom: 'I feel like a ___ out of water here.' (uncomfortable/out of place)",
          options: ["fish", "bird", "cat"],
          a: "fish",
          successtip: "✓ FISH OUT OF WATER! Immigrant feeling! 🐟 Feeling out of place = fuera de tu elemento. Very relatable for newcomers!"
        }
      ]
    },

    // SET 7: Comparing Intensities
    {
      id: "m4-u7-set7",
      title: "Set 7: Intensity Comparisons",
      questions: [
        {
          type: "match",
          q: "Match situation to best intensifier:",
          pairs: [
            { left: "Minor problem", right: "a bit annoyed" },
            { left: "Medium issue", right: "pretty frustrated" },
            { left: "Major disaster", right: "SO angry" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Boss is 10 minutes late: 'I'm ___ annoyed.' (minor)",
          options: ["a bit", "SO", "absolutely"],
          a: "a bit"
        },
        {
          type: "input",
          q: "Missed bus: 'I'm ___ frustrated.' (medium - pretty)",
          a: "pretty"
        },
        {
          type: "order",
          q: "Ordena: Lost your job: 'Estoy TAN enojado'",
          words: ["I'm", "SO", "angry!"],
          a: "I'm SO angry!"
        },
        {
          type: "mc",
          q: "Which expresses MOST happiness? Got permanent residence:",
          options: [
            "I'm SO over the moon!",
            "I'm pretty happy.",
            "I'm a bit pleased."
          ],
          a: "I'm SO over the moon!",
          tip: "Multiple intensifiers! 'SO over the moon' = combining for maximum emotion! Very expressive!"
        },
        {
          type: "input",
          q: "Small raise at work: 'I'm ___ happy.' (kind of/kinda)",
          a: "kind of / kinda"
        },
        {
          type: "mc",
          q: "20-hour work week: 'I'm ___ exhausted.'",
          options: ["totally", "a little", "not very"],
          a: "totally"
        },
        {
          type: "order",
          q: "Ordena: Minor delay: 'Estoy un poco impaciente'",
          words: ["I'm", "a", "bit", "impatient."],
          a: "I'm a bit impatient."
        },
        {
          type: "input",
          q: "Dream job offer: 'I'm ___ excited!' (absolutely/totally)",
          a: "absolutely / totally"
        },
        {
          type: "mc",
          q: "Which shows appropriate intensity? Small mistake at work:",
          options: [
            "I'm a little embarrassed",
            "I'm SO mortified!",
            "I'm absolutely devastated!"
          ],
          a: "I'm a little embarrassed",
          successtip: "✓ APPROPRIATE INTENSITY! Key skill! 🎯 Match intensity to situation = natural English. Overreacting = dramatic. Underreacting = dismissive!"
        }
      ]
    },

    // SET 8: Real Conversations with Intensifiers
    {
      id: "m4-u7-set8",
      title: "Set 8: Natural Dialogues",
      questions: [
        {
          type: "mc",
          q: "A: 'How was work?' B: 'Ugh, I'm ___ tired!' (emphasizing)",
          options: ["SO", "a bit", "not very"],
          a: "SO"
        },
        {
          type: "input",
          q: "A: 'Are you okay?' B: 'Yeah, just ___ stressed.' (kinda - downplaying)",
          a: "kinda / kind of"
        },
        {
          type: "order",
          q: "Ordena: A: '¿Cómo te sientes?' B: 'Estoy súper emocionado'",
          words: ["A:", "How", "are", "you", "feeling?", "B:", "I'm", "super", "excited!"],
          a: "A: How are you feeling? B: I'm super excited!"
        },
        {
          type: "mc",
          q: "A: 'Did you like the movie?' B: 'It was ___ good, not great.'",
          options: ["pretty", "SO", "absolutely"],
          a: "pretty"
        },
        {
          type: "input",
          q: "A: 'You look upset.' B: 'I'm ___ fed up with my landlord!' (really/so)",
          a: "really / so"
        },
        {
          type: "mc",
          q: "A: 'How's your new job?' B: 'I'm ___ burned out already.'",
          options: ["totally", "a little", "not very"],
          a: "totally",
          successtip: "✓ ALREADY! Time marker! ⏰ 'Already burned out' = tan pronto y ya agotado. Shows surprise at how fast it happened!"
        },
        {
          type: "order",
          q: "Ordena: A: '¿Nervioso por la entrevista?' B: 'Sí, estoy algo nervioso'",
          words: ["A:", "Nervous", "about", "the", "interview?", "B:", "Yeah,", "I'm", "kinda", "nervous."],
          a: "A: Nervous about the interview? B: Yeah, I'm kinda nervous."
        },
        {
          type: "input",
          q: "A: 'How do you feel about moving?' B: 'I'm ___ on edge about it.' (a bit/kind of)",
          a: "a bit / kind of"
        },
        {
          type: "mc",
          q: "A: 'You got the job!' B: '___! I'm over the moon!'",
          options: ["OMG", "Yes", "Okay"],
          a: "OMG",
          successtip: "✓ OMG! Slang reaction! 😱 'Oh my God' = reacción de sorpresa/emoción. Very common! Also: 'No way!', 'Seriously?!', 'For real?!'"
        },
        {
          type: "mc",
          q: "A: 'Are you mad?' B: 'Not mad, just ___ disappointed.'",
          options: ["a little", "SO", "extremely"],
          a: "a little",
          tip: "'Not X, just Y' = contrasting emotions. 'Not mad, just disappointed' = common parent phrase!"
        }
      ]
    },

    // SET 9: Advanced Combinations
    {
      id: "m4-u7-set9",
      title: "Set 9: Mixing Multiple Intensifiers",
      questions: [
        {
          type: "mc",
          q: "'I'm ___ SO tired that I can't think straight!'",
          options: ["like", "very", "quite"],
          a: "like",
          tip: "'Like' before 'SO' = extra emphasis (slang)! 'I'm like SO tired' = Gen Z/Millennial style. Very casual!"
        },
        {
          type: "input",
          q: "Complete: 'She's ___ totally fed up with waiting.' (just - emphasis)",
          a: "just"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy realmente muy emocionado'",
          words: ["I'm", "really", "very", "excited."],
          a: "I'm really very excited.",
          tip: "Double intensifiers! 'Really very' = two levels. Emphasis on emphasis! Casual speech pattern."
        },
        {
          type: "mc",
          q: "'I'm not just tired, I'm ___ exhausted!'",
          options: ["absolutely", "a bit", "kinda"],
          a: "absolutely",
          successtip: "✓ ESCALATION! Rhetorical technique! 📈 'Not just X, I'm Y' = increasing intensity. 'Not just tired, ABSOLUTELY exhausted' = dramatic effect!"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ pretty stressed these days.' (getting - continuous change)",
          a: "getting"
        },
        {
          type: "mc",
          q: "'I'm feeling ___ overwhelmed lately.'",
          options: ["increasingly", "just", "not"],
          a: "increasingly",
          tip: "'Increasingly' = cada vez más. Shows gradual increase! 'Increasingly tired/stressed' = common pattern."
        },
        {
          type: "order",
          q: "Ordena: 'Estoy cada vez más frustrado con la situación'",
          words: ["I'm", "getting", "more", "and", "more", "frustrated", "with", "the", "situation."],
          a: "I'm getting more and more frustrated with the situation.",
          successtip: "✓ MORE AND MORE! Progressive pattern! 📊 'More and more X' = cada vez más. Shows escalating emotion over time!"
        },
        {
          type: "input",
          q: "Complete: 'She's way ___ happy than before.' (too - excess emphasis)",
          a: "too"
        },
        {
          type: "mc",
          q: "Slang emphasis: 'I'm ___ freaking out right now!'",
          options: ["literally", "a bit", "not very"],
          a: "literally"
        },
        {
          type: "mc",
          q: "Complex emotion: 'I'm happy, but I'm ___ overwhelmed with everything.'",
          options: ["also kind of", "not", "never"],
          a: "also kind of",
          tip: "Combining opposites! 'Happy BUT also kind of overwhelmed' = mixed emotions. Very realistic!"
        }
      ]
    },

    // SET 10: Cultural Context & Situations
    {
      id: "m4-u7-set10",
      title: "Set 10: Advanced Situations",
      questions: [
        {
          type: "mc",
          q: "Canadian politeness: Complaint at restaurant: 'I'm ___ disappointed with the service.' (softening)",
          options: ["a bit", "SO", "absolutely"],
          a: "a bit",
          tip: "Canadian understatement! 'A bit disappointed' might mean VERY disappointed. Polite culture!"
        },
        {
          type: "input",
          q: "Job interview: 'I'm ___ excited about this opportunity.' (really/very - professional)",
          a: "really / very"
        },
        {
          type: "order",
          q: "Ordena: Talking to therapist: 'Me siento súper abrumado últimamente'",
          words: ["I've", "been", "feeling", "super", "overwhelmed", "lately."],
          a: "I've been feeling super overwhelmed lately.",
          successtip: "✓ PRESENT PERFECT CONTINUOUS! Advanced! 📚 'Have been feeling' = sentimiento continuo hasta ahora. Shows duration!"
        },
        {
          type: "mc",
          q: "Venting to friend: 'I'm ___ SO done with this job!' (completely finished/fed up - slang)",
          options: ["like", "not", "a bit"],
          a: "like",
          tip: "'Like SO done' = slang for 'totally fed up'. 'I'm done' = no aguanto más!"
        },
        {
          type: "input",
          q: "Immigration interview: 'I'm ___ grateful for this opportunity.' (absolutely - formal)",
          a: "absolutely"
        },
        {
          type: "mc",
          q: "Texting friend: 'Omg I'm ___ dead! 💀' (slang for extremely tired/laughing)",
          options: ["literally", "not", "quite"],
          a: "literally",
          successtip: "✓ LITERALLY DEAD! Gen Z slang! 💀 Doesn't mean actually dead! Means 'dying from laughter' or 'so tired'. Emoji culture!"
        },
        {
          type: "order",
          q: "Ordena: Talking to boss: 'Estoy un poco preocupado por la carga de trabajo'",
          words: ["I'm", "a", "little", "concerned", "about", "the", "workload."],
          a: "I'm a little concerned about the workload.",
          tip: "Professional understatement! 'A little concerned' = diplomatic way to complain to boss. 'Concerned' more formal than 'worried'."
        },
        {
          type: "input",
          q: "Mental health: 'I'm not okay, I'm ___ struggling.' (really - vulnerability)",
          a: "really"
        },
        {
          type: "mc",
          q: "Celebrating visa approval: 'I'm ___ over the moon and absolutely thrilled!' (multiple intensifiers)",
          options: ["totally", "a bit", "not"],
          a: "totally",
          successtip: "✓ STACKING INTENSIFIERS! Maximum emotion! 🎉 'Totally over the moon AND absolutely thrilled' = all the happiness words! When one isn't enough!"
        },
        {
          type: "mc",
          q: "Final integration: 'I used to be ___ overwhelmed when I arrived, but now I'm ___ confident, though I still get ___ homesick sometimes.'",
          options: [
            "SO, pretty, a bit",
            "a bit, SO, pretty",
            "pretty, a bit, SO"
          ],
          a: "SO, pretty, a bit",
          successtip: "✓ UNIT 7 COMPLETE! Intensifier mastery! 🎯 You can now express emotions with perfect intensity! From 'a bit' to 'SO' to 'absolutely' + phrasal verbs + idioms = you sound NATIVE! From basic 'I'm happy' to 'I'm like SO over the moon and totally freaking out!' = authentic English! 🔥💪🇨🇦"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: My Toronto Journey (Native Expression)
// ===================================================================
readingSet: {
  id: "m4-u7-reading",
  title: "Reading: My Toronto Journey",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Arrival (preguntas 2 y 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Beginning**

Hey! So I've been living in Toronto for like three years now, and honestly, my feelings about it have changed **SO** much. When I first arrived, I was absolutely terrified. 

Everything was super overwhelming - the subway system, the weather, the fast pace. I was literally freaking out every single day. I felt like a **fish out of water.**`
    },
    {
      type: "mc",
      q: "¿Qué idiom usa el autor para describir que se sentía totalmente fuera de lugar?",
      options: [
        "Fish out of water",
        "Over the moon",
        "Down in the dumps"
      ],
      a: "Fish out of water",
      successtip: "✓ IDIOM!\n'A fish out of water' = sentirse fuera de lugar o incómodo en un ambiente nuevo. 🐟🚫💦"
    },
    {
      type: "mc",
      q: "Según el primer párrafo, ¿cuál era el sentimiento inicial del autor?",
      options: [
        "Terrified",
        "Happy",
        "Bored"
      ],
      a: "Terrified",
      successtip: "✓ EXTREME ADJECTIVE!\n'Terrified' es mucho más fuerte que 'scared'. ¡Estaba muy asustado! 😱"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Struggle & Burnout (pregunta 3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The First Months**

After a few months, I was still pretty homesick, but I was getting more and more comfortable. I was kinda okay with my survival job at Tim Hortons, though I was totally fed up with being so tired all the time. I was working crazy hours and I was absolutely **burned out.**

But then things started changing. I'm not gonna lie, I was really struggling with my mental health. I was increasingly stressed and I felt **down in the dumps** a lot. But I started seeing a therapist and talking about my feelings. That helped **SO** much.`
    },
    {
      type: "mc",
      q: "¿Qué expresión (phrasal verb) usa para describir que estaba agotado por el exceso de trabajo?",
      options: ["Burned out", "Fed up", "Freaking out"],
      a: "Burned out",
      successtip: "✓ BURNED OUT!\nEstar 'quemado' o exhausto por el trabajo. Muy común en el mundo laboral. 🔥😫"
    },
    {
      type: "mc",
      q: "En la frase 'I felt down in the dumps', ¿cómo se sentía el autor?",
      options: [
        "Triste o deprimido",
        "Enojado",
        "Cansado"
      ],
      a: "Triste o deprimido",
      successtip: "✓ IDIOM!\n'Down in the dumps' = sentirse triste o desanimado. 😔"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: The Present (preguntas 4, 6, 7 y 8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Three Years Later**

Now? I'm pretty happy, actually! I'm still a bit homesick during holidays, and I'm not very fond of the winter (I'm **SO done with snow!**), but overall, I'm quite content. 

I'm really proud of how far I've come. I'm **over the moon** because my family is coming to visit next month! I used to be super anxious about everything, but now I'm just kind of chill about most things. I'm absolutely certain that moving here was the right decision.`
    },

    

    {
      type: "mc",
      q: "El autor usa 'SO' muchas veces. ¿Cuál es la función de esa palabra en este texto casual?",
      options: [
        "Dar énfasis emocional (como decir 'muy' o 'tan')",
        "Ser formal y profesional",
        "Indicar una consecuencia matemática"
      ],
      a: "Dar énfasis emocional (como decir 'muy' o 'tan')",
      successtip: "✓ EMOTIONAL EMPHASIS!\n'SO' es el rey del énfasis en el inglés hablado. 'SO tired', 'SO happy'. 💥"
    },
    {
      type: "mc",
      q: "El autor dice 'I'm over the moon'. ¿Por qué se siente así?",
      options: [
        "Porque su familia viene de visita",
        "Porque consiguió un nuevo trabajo",
        "Porque terminó el invierno"
      ],
      a: "Porque su familia viene de visita",
      successtip: "✓ OVER THE MOON!\nSignifica estar extremadamente feliz o radiante de alegría. 🌙✨"
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión slang 'I'm SO done with snow'?",
      options: [
        "Que ya no aguanta más la nieve (está harto)",
        "Que ya terminó de limpiar la nieve",
        "Que le gusta mucho la nieve"
      ],
      a: "Que ya no aguanta más la nieve (está harto)",
      tip: "'I'm done with [algo]' = Ya no quiero nada que ver con eso, estoy harto. 💡",
      successtip: "✓ FED UP!\nEs una forma muy común y casual de decir que perdiste la paciencia con algo. ❄️❌"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Advice & Analysis (preguntas 1, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Advice for Newcomers**

To anyone just arriving: you're probably feeling **totally** overwhelmed right now, and that's completely normal. You might be on edge all the time, or feeling a little lost. 

But trust me, you're going to be okay. You're literally going through one of the hardest things a person can do, and you're **SO** brave for doing it!`
    },
    {
      type: "mc",
      q: "El texto usa palabras como 'absolutely', 'super', 'literally', 'pretty' y 'kinda'. ¿Cómo se llaman?",
      options: [
        "Intensifiers (modifican la fuerza del adjetivo)",
        "Verbos de acción",
        "Preposiciones de lugar"
      ],
      a: "Intensifiers (modifican la fuerza del adjetivo)",
      successtip: "✓ INTENSIFIERS!\nPalabras que 'suben' o 'bajan' el volumen de lo que sientes. ¡Usaste más de 15 en la lectura! 📈"
    },
    {
      type: "mc",
      q: "En conclusión, el tono de este texto es:",
      options: [
        "Casual, auténtico y de apoyo (como hablar con un amigo)",
        "Formal, académico y serio",
        "Negativo, pesimista y triste"
      ],
      a: "Casual, auténtico y de apoyo (como hablar con un amigo)",
      successtip: "✓ AUTHENTIC VOICE! 🎉\n¡Felicidades! Has comprendido un texto con inglés real: mezcla de modismos, intensidad y lenguaje emocional. 🏆"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u7-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match intensity level:",
        pairs: [
          { left: "Low", right: "a bit, a little, not very" },
          { left: "Medium", right: "pretty, quite, kind of" },
          { left: "High", right: "very, really, SO, absolutely" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Order: Intensifier + ___",
        options: ["adjective", "verb", "noun"],
        a: "adjective",
        tip: "'Very tired' ✓, 'tired very' ❌. Intensifier BEFORE adjective always!"
      },
      {
        type: "mc",
        q: "Which is MOST intense?",
        options: ["I'm SO happy!", "I'm pretty happy.", "I'm a bit happy."],
        a: "I'm SO happy!"
      },
      {
        type: "input",
        q: "Phrasal verb for 'harto de': I'm ___ with this! (fed up)",
        a: "fed up"
      },
      {
        type: "mc",
        q: "Idiom for 'muy feliz':",
        options: ["over the moon", "down in the dumps", "on edge"],
        a: "over the moon"
      },
      {
        type: "order",
        q: "Ordena: 'Estoy súper cansado'",
        words: ["I'm", "super", "tired."],
        a: "I'm super tired."
      },
      {
        type: "mc",
        q: "'Burned out' significa:",
        options: [
          "Agotado por trabajo/estudio",
          "Quemado por el sol",
          "Enojado"
        ],
        a: "Agotado por trabajo/estudio"
      },
      {
        type: "input",
        q: "Slang for 'kind of': I'm ___ tired. (kinda)",
        a: "kinda"
      },
      {
        type: "mc",
        q: "'Literally' se usa para:",
        options: [
          "Emphasis (even when exaggerating - slang)",
          "Only for literal truth",
          "Formal writing"
        ],
        a: "Emphasis (even when exaggerating - slang)"
      },
      {
        type: "mc",
        q: "Final review: 'I used to be ___ overwhelmed, but now I'm ___ happy because I'm ___ getting used to life here, though I'm still ___ homesick sometimes.'",
        options: [
          "SO, pretty, really, a bit",
          "a bit, SO, a little, really",
          "pretty, SO, kinda, absolutely"
        ],
        a: "SO, pretty, really, a bit",
        successtip: "✓ UNIT 7 MASTERED! 🎉 You can now express emotions with PERFECT intensity using native intensifiers, phrasal verbs, idioms, and slang! From 'a bit tired' to 'literally SO burned out and over the moon' = you sound like a NATIVE SPEAKER! This is advanced emotional expression! 💪🔥🇨🇦"
      }
    ]
  }
};
